import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { customersListFixture, customerDetailFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-customers.md
 * Endpoints: /v1/admin/customers
 *
 * Auto-Sync läuft im Backend stündlich. UI zeigt `netbox_synced_at` damit
 * der Operator weiß ob Daten frisch sind, und exposed einen manuellen
 * Sync-Trigger.
 */

/** GET /v1/admin/customers?tenant_id=&search=&limit=&offset= */
export function useCustomersList({ tenantId = '', search = '', limit = 100, offset = 0 } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: [
      'nexus-customers',
      { tenantId, search, limit, offset },
      fixturesMode ? 'fixture' : 'live',
    ],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return customersListFixture({ tenantId, search });
      }
      const qs = new URLSearchParams();
      if (tenantId) qs.set('tenant_id', String(tenantId));
      if (search) qs.set('search', search);
      if (limit) qs.set('limit', String(limit));
      if (offset) qs.set('offset', String(offset));
      const res = await nexusFetch(`/v1/admin/customers?${qs.toString()}`);
      const list = res?.customers ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { customers: list.map(normalizeCustomer), raw: res };
    },
    staleTime: 30_000,
  });
}

/** GET /v1/admin/customers/:id → { customer, services, domains } */
export function useCustomerDetail(id) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-customer-detail', String(id ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!id,
    queryFn: async () => {
      const res = fixturesMode
        ? (await fixtureDelay(), customerDetailFixture(id))
        : await nexusFetch(`/v1/admin/customers/${encodeURIComponent(id)}`);
      const customer = res?.customer ?? res;
      return {
        customer: normalizeCustomer(customer),
        services: (res?.services ?? []).map(normalizeService),
        domains: (res?.domains ?? []).map(normalizeDomain),
      };
    },
  });
}

/** POST /v1/admin/customers/sync?plugin=… — manueller Sync-Trigger */
export function useTriggerCustomerSync() {
  return useApiMutation({
    live: (_fetch, { plugin } = {}) => {
      const qs = plugin ? `?plugin=${encodeURIComponent(plugin)}` : '';
      return nexusFetch(`/v1/admin/customers/sync${qs}`, { method: 'POST' });
    },
    fixture: ({ plugin } = {}) => ({
      ok: true,
      plugin: plugin ?? 'all',
      job_id: `sync-${Date.now()}`,
    }),
    invalidate: ['nexus-customers', 'nexus-customer-detail'],
    successMessage: (res, { plugin } = {}) =>
      `NetBox-Sync angestoßen${plugin ? ` (Plugin ${plugin})` : ''}${
        res?.job_id ? ` · Job ${res.job_id}` : ''
      }.`,
  });
}

/**
 * Backend liefert teilweise snake_case (`netbox_synced_at`, `external_id`)
 * oder camelCase je nach Plugin. Wir spiegeln beide damit die UI mit einem
 * stabilen Set arbeitet.
 */
function normalizeCustomer(c) {
  if (!c || typeof c !== 'object') return c;
  const netbox_tenant_id =
    c.netbox_tenant_id ?? c.netboxTenantId ?? null;
  return {
    ...c,
    display_name: c.display_name ?? c.displayName ?? null,
    external_id: c.external_id ?? c.externalId ?? null,
    // Backend liefert per Launch-Briefing 2026-06-14 `netbox_tenant_id`
    // (numerisch) statt eines Slugs. Wir tragen beides; ältere Snapshots
    // mit `netbox_slug` bleiben gültig, neue Daten bauen den Anzeige-Slug
    // aus der Tenant-ID (`customer-<id>` ist die Nexus-Konvention).
    netbox_tenant_id,
    netbox_slug:
      c.netbox_slug ??
      c.netboxSlug ??
      (netbox_tenant_id != null ? `customer-${netbox_tenant_id}` : null),
    netbox_synced_at: c.netbox_synced_at ?? c.netboxSyncedAt ?? null,
    services_count: c.services_count ?? c.servicesCount ?? c.services?.length ?? 0,
    domains_count: c.domains_count ?? c.domainsCount ?? c.domains?.length ?? 0,
    tenant_id: c.tenant_id ?? c.tenantId ?? null,
  };
}

function normalizeService(s) {
  if (!s || typeof s !== 'object') return s;
  return {
    ...s,
    plugin_id: s.plugin_id ?? s.pluginId ?? s.kind ?? null,
    kind: s.kind ?? null,
    account_id: s.account_id ?? s.accountId ?? null,
    subscription_id: s.subscription_id ?? s.subscriptionId ?? null,
    external_id: s.external_id ?? s.externalId ?? null,
    status: s.status ?? null,
  };
}

function normalizeDomain(d) {
  if (!d || typeof d !== 'object') return d;
  return {
    ...d,
    // Live-Shape: `kind` ('primary'/'alias'). Frühere Snapshots: `type`.
    kind: d.kind ?? d.type ?? null,
    type: d.type ?? d.kind ?? null,
    source: d.source ?? null,
  };
}

export function getNetboxBaseUrl() {
  if (typeof window === 'undefined') return null;
  return window.__ORBIT_CONFIG__?.netboxBaseUrl ?? null;
}

export function buildNetboxTenantUrl(slug) {
  const base = getNetboxBaseUrl();
  if (!base || !slug) return null;
  return `${base.replace(/\/$/, '')}/tenancy/tenants/?q=${encodeURIComponent(slug)}`;
}
